const KEY = 'cf-newsletter-visit';
const TIMEOUT = 30 * 60 * 1000;

export function visitSession(storage, now = Date.now()) {
  let state;
  try { state = JSON.parse(storage?.getItem(KEY) || 'null'); } catch { /* Storage may be blocked. */ }
  if (!state || !Number.isFinite(state.lastActive) || now - state.lastActive >= TIMEOUT) {
    state = { lastActive: now, shown: false };
  }
  const save = () => { try { storage?.setItem(KEY, JSON.stringify(state)); } catch { /* Keep in-memory state. */ } };
  state.lastActive = now;
  save();
  return {
    get shown() { return Boolean(state.shown); },
    markShown() { state.shown = true; save(); },
    touch(time = Date.now()) { state.lastActive = time; save(); },
  };
}

export function shouldOpen(session, elapsed, subscribed) {
  return !subscribed && !session.shown && elapsed >= 10000;
}

export function submissionFeedback(button, status) {
  const label = button.textContent;
  let pending = false;
  return {
    get pending() { return pending; },
    start() {
      pending = true;
      button.textContent = 'Subscribing…';
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('aria-disabled', 'true');
      status.textContent = 'Submitting your subscription. Please wait…';
    },
    slow() {
      if (pending) status.textContent = 'Shopify is still processing your subscription. Please wait and complete any verification shown.';
    },
    reset() {
      pending = false;
      button.textContent = label;
      button.removeAttribute('aria-busy');
      button.removeAttribute('aria-disabled');
      status.textContent = '';
    },
  };
}

function init(root) {
  if (root.dataset.initialized) return;
  root.dataset.initialized = 'true';
  const dialog = root.querySelector('dialog');
  if (!dialog?.showModal) return;
  const form = root.closest('form');
  const button = root.querySelector('button[type="submit"]');
  const status = root.querySelector('[data-cf-submit-status]');
  if (form && button && status) {
    const feedback = submissionFeedback(button, status);
    let slowTimer;
    const start = () => {
      if (feedback.pending) return;
      feedback.start();
      slowTimer = setTimeout(() => feedback.slow(), 12000);
    };
    // Give feedback before Shopify's captcha handling, without replacing it.
    button.addEventListener('click', event => {
      if (feedback.pending) { event.preventDefault(); return; }
      if (form.checkValidity()) start();
    }, { capture: true });
    // Do not cancel submit: captcha may legitimately resubmit the same form.
    form.addEventListener('submit', start, { capture: true });
    window.addEventListener('pageshow', event => {
      if (event.persisted) { clearTimeout(slowTimer); feedback.reset(); }
    });
  }
  let storage;
  try { storage = window.sessionStorage; } catch { /* Use memory when unavailable. */ }
  const session = visitSession(storage);
  const subscribed = () => {
    try { return root.dataset.subscribed === 'true' || window.localStorage.getItem('cf-newsletter-subscribed') === 'true'; }
    catch { return root.dataset.subscribed === 'true'; }
  };
  const open = () => { session.markShown(); dialog.showModal(); };
  if (root.dataset.success === 'true') {
    try { window.localStorage.setItem('cf-newsletter-subscribed', 'true'); } catch { /* Server still confirmed signup. */ }
    open();
  } else if (root.dataset.errors === 'true') {
    open();
  } else {
    const start = performance.now();
    const check = () => {
      if (document.visibilityState === 'visible' && shouldOpen(session, performance.now() - start, subscribed())) open();
    };
    setTimeout(check, 10000);
    document.addEventListener('visibilitychange', check);
  }
  let touched = 0;
  for (const name of ['pointerdown', 'keydown', 'scroll']) {
    document.addEventListener(name, () => {
      const now = Date.now();
      if (now - touched > 10000) { touched = now; session.touch(now); }
    }, { passive: true });
  }
  root.querySelectorAll('[data-cf-close]').forEach(button => button.addEventListener('click', () => dialog.close()));
  dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); });
}

if (typeof document !== 'undefined') {
  document.querySelectorAll('[data-cf-newsletter]').forEach(init);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-cf-newsletter]').forEach(init));
}
